import { ProfileCard } from "../../types/profile";
import { getGenderPlaceholder } from "../../helpers/matches.helper";
import { UPLOAD_URL } from "../../utils/constants";

export const parseNumericValue = (
  value: string | number | undefined | null,
): number | null => {
  if (value === undefined || value === null) {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const cleaned = value.trim();
  if (!cleaned) {
    return null;
  }

  const numeric = Number(cleaned.replace(/[^\d.-]/g, ""));
  return Number.isNaN(numeric) ? null : numeric;
};

export const clampPercentage = (value: number) =>
  Math.min(Math.max(value, 0), 100);

export const parseCompletionValue = (
  value: string | number | undefined | null,
): number | null => {
  if (value === undefined || value === null) {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? clampPercentage(value) : null;
  }

  const cleaned = value.trim();
  if (!cleaned) {
    return null;
  }

  const numeric = Number.parseFloat(cleaned.replace(/[^\d.]/g, ""));

  return Number.isNaN(numeric)
    ? null
    : clampPercentage(numeric);
};

export const calculateAgeFromDob = (
  dob?: string | null,
): number | null => {
  if (!dob) {
    return null;
  }

  const birthDate = new Date(dob);

  if (Number.isNaN(birthDate.getTime())) {
    return null;
  }

  const today = new Date();

  let age = today.getFullYear() - birthDate.getFullYear();

  const monthDifference =
    today.getMonth() - birthDate.getMonth();

  if (
    monthDifference < 0 ||
    (monthDifference === 0 &&
      today.getDate() < birthDate.getDate())
  ) {
    age -= 1;
  }

  return age;
};

export const normalizeProfileCard = (
  profile: any,
  index: number,
  sectionKey: string,
): ProfileCard => {
  const fallbackId = `${sectionKey}-${index}`;

  const id =
    profile?.maritalId ??
    profile?.martialId ??
    fallbackId;

  const recordId =
    profile?._id ??
    profile?.id ??
    undefined;

  const name =
    profile?.fullName ||
    `Profile ${index + 1}`;

  const age =
    profile?.age ??
    calculateAgeFromDob(profile?.dateOfBirth) ??
    "";

  const rawImage =
    profile?.image ??
    profile?.profileImage ??
    profile?.photo ??
    profile?.avatar ??
    null;

  const image =
    typeof rawImage === "string" &&
    rawImage.trim().length > 0
      ? rawImage.startsWith("http")
        ? rawImage
        : `${UPLOAD_URL}/users/profile_pic/${rawImage.replace(
            /^\/+/,
            "",
          )}`
      : getGenderPlaceholder(profile?.gender);

  const bureau =
    profile?.bureau ??
    profile?.bureauName ??
    profile?.mediatorName ??
    profile?.createdByBureau;

  const location =
    profile?.location ||
    [
      profile?.city,
      profile?.district,
      profile?.state,
      profile?.country,
    ]
      .filter(Boolean)
      .join(", ");

  const completeness = profile?.completeness;

  let matchScore: string | number | undefined = completeness;

  if (
    typeof completeness === "number" &&
    Number.isFinite(completeness)
  ) {
    matchScore = `${completeness.toFixed(1)}%`;
  } else if (
    typeof completeness === "string" &&
    completeness.trim()
  ) {
    if (!completeness.includes("%")) {
      const numeric = Number(
        completeness.replace(/[^\d.-]/g, ""),
      );

      matchScore = Number.isNaN(numeric)
        ? completeness
        : `${numeric.toFixed(1)}%`;
    }
  }

  return {
    id,
    recordId,
    maritalId:
      profile?.maritalId ??
      profile?.martialId ??
      undefined,
    name,
    age,
    height: profile?.height ?? "",
    image,
    imagePrivacy: profile?.imagePrivacy,
    maritalStatus: profile?.maritalStatus,
    education: profile?.education,
    occupation: profile?.occupation,
    annualIncome: profile?.annualIncome,
    country: profile?.country,
    state: profile?.state,
    district: profile?.district,
    status: profile?.status,
    bureau,
    bureauName:
      profile?.bureauName ??
      bureau ??
      "Self Registered",
    mobileNumber: profile?.mobileNumber,
    location: location || bureau || undefined,
    matchScore,
    completion: completeness,
    paymentStatus: profile?.paymentStatus,
    caste: profile?.caste,
    citizenship: profile?.citizenship,
  };
};

export const getProfileIdentifier = (
  profile: ProfileCard,
) =>
  profile.recordId ??
  profile.id ??
  profile.name;